import React, { useState } from 'react';
import WaButton from '../../wa/WaButton';
import MyAvailability from '../Work/MyAvailability';
import { DAY_KEYS, DAY_LABELS, describeWindows, teacherCap } from './schedulingModel';

/**
 * The manage layer — student rules, time off, weekly availability.
 *
 * These recede behind the board because they are touched rarely, but nothing
 * was removed: every capability the pre-rebuild page had is still here, just
 * one click away instead of occupying the top of the screen.
 */

/* ─────────────────────────── student rules ─────────────────────────── */

export function StudentRulesPanel({
	rules,
	teachers,
	teacherNameById,
	isAdmin,
	saving,
	onAdd,
	onRemove,
}) {
	const [studentMatch, setStudentMatch] = useState('');
	const [blockedTeacherId, setBlockedTeacherId] = useState('');
	const [preferredTeacherId, setPreferredTeacherId] = useState('');
	const [scope, setScope] = useState('day');

	const submit = async () => {
		const ok = await onAdd({ studentMatch, blockedTeacherId, preferredTeacherId, scope });
		if (ok) {
			setStudentMatch('');
			setBlockedTeacherId('');
			setPreferredTeacherId('');
			setScope('day');
		}
	};

	return (
		<div className='ts-panel'>
			<p className='ts-panel__hint'>
				Blocks a teacher for one student — whole day or that slot only.
			</p>

			<ul className='ts-rulelist'>
				{rules.length === 0 ? (
					<li className='ts-rulelist__empty'>No rules yet.</li>
				) : (
					rules.map((rule) => (
						<li className='ts-rulelist__row' key={rule.id}>
							<span className='ts-rulelist__text'>
								<strong>{rule.studentMatch}</strong>
								<span className='ts-chip is-rule'>
									Never {teacherNameById.get(rule.blockedTeacherId) || rule.blockedTeacherId} ·{' '}
									{rule.scope === 'timeslot' ? 'that slot' : 'whole day'}
								</span>
								{rule.preferredTeacherId && (
									<span className='ts-rulelist__prefer'>
										Prefers {teacherNameById.get(rule.preferredTeacherId) || rule.preferredTeacherId}
									</span>
								)}
							</span>
							{isAdmin && (
								<WaButton size='small' onClick={() => onRemove(rule.id)}>
									Remove
								</WaButton>
							)}
						</li>
					))
				)}
			</ul>

			{isAdmin && (
				<div className='ts-form'>
					<label className='ts-field'>
						<span>Student name contains</span>
						<input
							type='text'
							value={studentMatch}
							onChange={(event) => setStudentMatch(event.target.value)}
							placeholder='e.g. Maya'
							disabled={saving}
						/>
					</label>
					<label className='ts-field'>
						<span>Blocked teacher</span>
						<select
							value={blockedTeacherId}
							onChange={(event) => setBlockedTeacherId(event.target.value)}
							disabled={saving}>
							<option value=''>Select…</option>
							{teachers.map((teacher) => (
								<option key={teacher.id} value={teacher.id}>
									{teacher.name || teacher.id}
								</option>
							))}
						</select>
					</label>
					<label className='ts-field'>
						<span>Scope</span>
						<select value={scope} onChange={(event) => setScope(event.target.value)} disabled={saving}>
							<option value='day'>Whole day</option>
							<option value='timeslot'>That slot only</option>
						</select>
					</label>
					<label className='ts-field'>
						<span>Preferred cover (optional)</span>
						<select
							value={preferredTeacherId}
							onChange={(event) => setPreferredTeacherId(event.target.value)}
							disabled={saving}>
							<option value=''>—</option>
							{teachers.map((teacher) => (
								<option key={teacher.id} value={teacher.id}>
									{teacher.name || teacher.id}
								</option>
							))}
						</select>
					</label>
					<WaButton variant='brand' onClick={submit} disabled={saving}>
						{saving ? 'Saving…' : 'Add rule'}
					</WaButton>
				</div>
			)}
		</div>
	);
}

/* ───────────────────────────── time off ───────────────────────────── */

export function TimeOffPanel({ entries, teachers, teacherNameById, isAdmin, saving, onAdd, onRemove }) {
	const [teacherId, setTeacherId] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [startTime, setStartTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [reason, setReason] = useState('');

	const submit = async () => {
		const ok = await onAdd({ teacherId, startDate, endDate, startTime, endTime, reason });
		if (ok) {
			setTeacherId('');
			setStartDate('');
			setEndDate('');
			setStartTime('');
			setEndTime('');
			setReason('');
		}
	};

	return (
		<div className='ts-panel'>
			<p className='ts-panel__hint'>Hard blocks — never scheduled over.</p>

			<ul className='ts-rulelist'>
				{entries.length === 0 ? (
					<li className='ts-rulelist__empty'>No time off scheduled.</li>
				) : (
					entries.map((entry) => (
						<li className='ts-rulelist__row' key={entry.id}>
							<span className='ts-rulelist__text'>
								<strong>{teacherNameById.get(entry.teacherId) || entry.teacherId}</strong>
								<span className='ts-rulelist__when'>
									{entry.date}
									{entry.startTime && entry.endTime
										? ` · ${describeWindows([{ start: entry.startTime, end: entry.endTime }])} · timed window`
										: ' · full day'}
								</span>
								{entry.reason && <span className='ts-rulelist__prefer'>{entry.reason}</span>}
							</span>
							{isAdmin && (
								<WaButton size='small' onClick={() => onRemove(entry.id)}>
									Remove
								</WaButton>
							)}
						</li>
					))
				)}
			</ul>

			{isAdmin && (
				<div className='ts-form'>
					<label className='ts-field'>
						<span>Teacher</span>
						<select
							value={teacherId}
							onChange={(event) => setTeacherId(event.target.value)}
							disabled={saving}>
							<option value=''>Select…</option>
							{teachers.map((teacher) => (
								<option key={teacher.id} value={teacher.id}>
									{teacher.name || teacher.id}
								</option>
							))}
						</select>
					</label>
					<label className='ts-field'>
						<span>From</span>
						<input
							type='date'
							value={startDate}
							onChange={(event) => setStartDate(event.target.value)}
							disabled={saving}
						/>
					</label>
					<label className='ts-field'>
						<span>To (optional)</span>
						<input
							type='date'
							value={endDate}
							onChange={(event) => setEndDate(event.target.value)}
							disabled={saving}
						/>
					</label>
					{/* Card note 5: the pre-rebuild form had date fields only, so the
					    timed windows that shipped 2026-08-25 were unreachable from the
					    admin side even though the list rendered them. Both times or
					    neither, single day only — the callable enforces the same rule. */}
					<label className='ts-field'>
						<span>From time (optional)</span>
						<input
							type='time'
							value={startTime}
							onChange={(event) => setStartTime(event.target.value)}
							disabled={saving}
						/>
					</label>
					<label className='ts-field'>
						<span>Until time (optional)</span>
						<input
							type='time'
							value={endTime}
							onChange={(event) => setEndTime(event.target.value)}
							disabled={saving}
						/>
					</label>
					<label className='ts-field ts-field--wide'>
						<span>Reason (optional)</span>
						<input
							type='text'
							value={reason}
							onChange={(event) => setReason(event.target.value)}
							disabled={saving}
						/>
					</label>
					<WaButton variant='brand' onClick={submit} disabled={saving}>
						{saving ? 'Saving…' : 'Add time off'}
					</WaButton>
					<p className='ts-panel__hint ts-panel__hint--full'>
						A timed window covers one day. Recital days and “must-work” events refuse
						teacher time-off requests automatically — only an admin can override here.
					</p>
				</div>
			)}
		</div>
	);
}

/* ──────────────────────── weekly availability ──────────────────────── */

/* ─────────────────────────── lesson caps ─────────────────────────── */

/**
 * Per-teacher lesson cap for a generated run.
 *
 * The cap is SOFT by decision (Anthony, 2026-08-30): blocks are assigned whole,
 * so a lesson count almost never lands on the limit, and his ruling was to
 * prefer other teachers but take the block rather than leave a hole — with the
 * overage shown on the load card. The copy here says that out loud, because a
 * control labelled "maximum" that can be exceeded is a lie otherwise.
 *
 * Empty means uncapped, and that is the default for everyone: adding this
 * feature changes no existing behaviour until a number is deliberately typed.
 */
export function LessonCapPanel({ teachers, isAdmin, saving, onSave }) {
	const [drafts, setDrafts] = useState({});

	const valueFor = (teacher) => {
		if (Object.prototype.hasOwnProperty.call(drafts, teacher.id)) return drafts[teacher.id];
		const cap = teacherCap(teacher);
		return cap === null ? '' : String(cap);
	};

	const dirty = (teacher) => {
		const cap = teacherCap(teacher);
		return valueFor(teacher) !== (cap === null ? '' : String(cap));
	};

	return (
		<div className='ts-panel'>
			<p className='ts-panel__hint'>
				Most lessons a teacher should get on a generated schedule. Leave blank for no
				limit. The generator gives a capped teacher a block only when nobody else can
				take it, so a run can finish over — the load card shows by how much.
			</p>

			<ul className='ts-caplist'>
				{teachers.map((teacher) => (
					<li className='ts-caprow' key={teacher.id}>
						<label className='ts-caprow__name' htmlFor={`cap-${teacher.id}`}>
							{teacher.name || teacher.id}
						</label>
						<input
							id={`cap-${teacher.id}`}
							className='ts-caprow__input'
							type='number'
							min='0'
							step='1'
							inputMode='numeric'
							placeholder='No limit'
							value={valueFor(teacher)}
							disabled={!isAdmin || saving}
							onChange={(e) =>
								setDrafts((d) => ({ ...d, [teacher.id]: e.target.value }))
							}
						/>
						<WaButton
							size='small'
							variant='brand'
							disabled={!isAdmin || saving || !dirty(teacher)}
							onClick={() => {
								// Guard the handler as well as the control: a wa-* `disabled`
								// is applied as a property AFTER the element upgrades, so a
								// click landing in that window still fires (CLAUDE.md).
								if (!isAdmin || saving || !dirty(teacher)) return;
								const raw = valueFor(teacher).trim();
								const next = raw === '' ? null : Number(raw);
								if (next !== null && (!Number.isFinite(next) || next < 0)) return;
								onSave(teacher, next);
								setDrafts((d) => {
									const copy = { ...d };
									delete copy[teacher.id];
									return copy;
								});
							}}>
							Save
						</WaButton>
					</li>
				))}
			</ul>
		</div>
	);
}

export function AvailabilityPanel({ teachers, teacherNameById, onChanged }) {
	const [editingId, setEditingId] = useState('');

	return (
		<div className='ts-panel'>
			<p className='ts-panel__hint'>
				Soft preference — the generator avoids these, review catches the rest.
			</p>

			<ul className='ts-availlist'>
				{teachers.map((teacher) => (
					<li className='ts-availrow' key={teacher.id}>
						<span className='ts-availrow__name'>{teacher.name || teacher.id}</span>
						<span className='ts-availrow__days'>
							{DAY_KEYS.map((dayKey) => {
								const value = teacher.availability?.[dayKey];
								const off = value === false;
								const limited = Array.isArray(value);
								const blocked = Array.isArray(teacher.unavailableBlocks?.[dayKey]);
								return (
									<span
										key={dayKey}
										className={`ts-availday${off ? ' is-off' : ''}${limited ? ' is-limited' : ''}${blocked ? ' is-blocked' : ''}`}
										title={
											off
												? 'Not available'
												: limited
													? `Limited: ${describeWindows(value)}`
													: blocked
														? `Blocked: ${describeWindows(teacher.unavailableBlocks[dayKey])}`
														: 'Available'
										}>
										<span className='ts-availday__label'>{DAY_LABELS[dayKey]}</span>
										<span className='ts-availday__state'>
											{off ? 'off' : limited ? describeWindows(value) : blocked ? 'blocked' : '—'}
										</span>
									</span>
								);
							})}
						</span>
						<WaButton
							size='small'
							onClick={() => setEditingId(editingId === teacher.id ? '' : teacher.id)}>
							{editingId === teacher.id ? 'Close' : 'Edit'}
						</WaButton>
					</li>
				))}
			</ul>

			{editingId && (
				<MyAvailability
					key={editingId}
					teacherId={editingId}
					teacherName={teacherNameById.get(editingId) || undefined}
					onChanged={onChanged}
				/>
			)}
		</div>
	);
}
